import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  TouchableOpacity,
  TouchableWithoutFeedback,
} from 'react-native';

import Icon from '@components/shared/Icon/Icon';
import { Tag } from '@components/shared/Tags/Tag';
import { MessageCheckin } from '@graphql/generated';
import { useLocation } from '@utils/locationUtils';

import { Box, Text } from '../Restyle';
import TagsCollection from './TagsCollection';

type CheckInProps = {
  checkin: MessageCheckin;
  error?: boolean;
  tags?: string[];
  onClose?: () => void;
  onLongPress?: () => void;
  showBorder?: boolean;
  isActivityItem?: boolean;
};

export const CheckInCard: React.FC<CheckInProps> = (props) => {
  const { t } = useTranslation('format');
  const {
    checkin,
    error,
    tags,
    onClose,
    onLongPress,
    showBorder,
    isActivityItem,
  } = props;
  const { address, city, state, zipcode, latitude, longitude, withinGeofence } =
    checkin;
  const { openAddress: openAddressFnc } = useLocation();

  const openAddress = () => {
    const newAddress = `${address} ${t('checkInCardLocation', {
      city,
      state,
      zipcode,
    })}`;
    openAddressFnc(newAddress, latitude, longitude);
  };

  if (isActivityItem) {
    return (
      <Box
        borderColor='grey02'
        backgroundColor='white'
        borderWidth={showBorder ? 1 : 0}
        borderRadius='xs'
        marginTop={Platform.OS === 'web' ? 'xs' : 'none'}
        width={252}>
        <Tag
          label={t(
            withinGeofence
              ? 'models:chat.checkin.checkedInForThisProject'
              : 'models:chat.checkin.locationDoesNotMatchWithProjectAddress'
          )}
          labelVariant='metadataSecondaryNew'
          variant={withinGeofence ? 'checkIn' : 'checkInError'}
          prefixMarginRight='xxs'
          prefix={
            withinGeofence ? (
              <Icon name='CheckCircle' variant='xxs' color='greenSecondary' />
            ) : (
              <Icon name='Info' variant='xxs' color='alertRed' />
            )
          }
          iconWidth={12}
          iconHeight={12}
          padding='xs'
          borderTopLeftRadius='xs'
          borderTopRightRadius='xs'
          borderBottomLeftRadius='none'
          borderBottomRightRadius='none'
        />
        <Box
          flex={1}
          flexDirection='row'
          alignItems='center'
          overflow='hidden'
          padding='xs'>
          <Icon variant='l' name='MapFlat' onPress={openAddress} />

          <Box marginLeft='xs' flex={1}>
            <TouchableWithoutFeedback onPress={openAddress}>
              <Box>
                <Text
                  color='grey04'
                  variant='metadataSecondaryNew'
                  numberOfLines={1}
                  ellipsizeMode='tail'>
                  {address}
                </Text>
                <Text
                  color='locationMeta'
                  variant='metadataSecondary'
                  marginTop='xxs'
                  numberOfLines={1}>
                  {city
                    ? t('checkInCardLocation', { city, state, zipcode })
                    : t('checkInCardLocationNoCity', { state, zipcode })}
                </Text>
              </Box>
            </TouchableWithoutFeedback>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <TouchableWithoutFeedback onLongPress={onLongPress}>
      <Box
        borderColor={error ? 'alertRed' : 'grey02'}
        backgroundColor='white'
        borderWidth={showBorder ? 1 : 0}
        borderRadius='xs'
        padding={Platform.OS === 'web' ? 'xs' : 'none'}
        marginVertical={Platform.OS === 'web' ? 'xs' : 'none'}
        overflow='hidden'>
        <Box flex={1} flexDirection='row' alignItems='center' overflow='hidden'>
          <Icon
            variant='l'
            name='MapFlat'
            backgroundColor='grey01'
            onPress={openAddress}
          />

          <Box marginLeft='xs' flex={1}>
            <TouchableWithoutFeedback onPress={openAddress}>
              <Box>
                <Text
                  color='blue'
                  variant='labelEmphasized14'
                  numberOfLines={1}
                  ellipsizeMode='tail'>
                  {address}
                </Text>
                <Text
                  color='locationMeta'
                  variant='metadata'
                  marginTop='xxxs'
                  numberOfLines={1}>
                  {city
                    ? t('checkInCardLocation', { city, state, zipcode })
                    : t('checkInCardLocationNoCity', { state, zipcode })}
                </Text>
              </Box>
            </TouchableWithoutFeedback>
          </Box>

          {onClose && (
            <TouchableOpacity
              accessibilityLabel='Remove'
              onPress={onClose}
              hitSlop={{ top: 10, bottom: 10, right: 10, left: 10 }}>
              <Icon
                name='X2'
                variant='l'
                marginTop='xxxs'
                color='textPrimary'
              />
            </TouchableOpacity>
          )}
        </Box>
        {error && (
          <Box flexDirection='row' marginTop='s'>
            <Icon variant='s' name='AlertCircle' color='alertRed' />
            <Text variant='metadata' color='alertRed' marginLeft='xs'>
              {t('models:chat.checkin.checkinError')}
            </Text>
          </Box>
        )}
        <Box marginTop='xxs'>
          {tags?.map((tagCollections) => {
            return tagCollections.map((item) => {
              const { project, tasks, author } = item;
              return (
                <Box key={project?.id}>
                  <TagsCollection
                    tagsCollection={{ project, tasks, author }}
                    showAuthor={false}
                    borderless
                  />
                </Box>
              );
            });
          })}
        </Box>
      </Box>
    </TouchableWithoutFeedback>
  );
};
